import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { wiegandToBolid } from '@/lib/bolid';

/**
 * Модалка создания/редактирования пропуска СКУД.
 *
 * Пропс mode:
 *   • 'create' + skudPersonId → POST /api/skud/persons/{id}/passes
 *   • 'edit'   + pass         → PUT  /api/skud/passes/{pass.skudId}
 *
 * Все изменения сохраняются локально (pending_sync=1). Outbound-воркер
 * отправит их в Orion в ближайшие 10 секунд.
 *
 * Дата хранится в БД с временем (DATETIME), но для UI достаточно даты —
 * пользователю проще. Время выставляется автоматически (начало дня).
 */
export default function SkudPassEditModal({ mode, skudPersonId, pass, onClose, onSaved }) {
  const isEdit = mode === 'edit';

  // Дефолты при создании: «Действует с» — сегодня, «по» — сегодня + 5 лет.
  // Срок типичной школьной карты — учебный цикл (1-4 класса), 5 лет с
  // запасом покрывают и сад, и начальную школу. При edit берём как есть.
  const [code, setCode]             = useState(pass?.code || '');
  const [startDate, setStartDate]   = useState(
    isEdit ? toDateInput(pass?.startDate) : isoToday()
  );
  const [endDate, setEndDate]       = useState(
    isEdit ? toDateInput(pass?.endDate) : isoTodayPlusYears(5)
  );
  const [accessLevelId, setAccess]  = useState(pass?.accessLevelId ?? '');
  const [comment, setComment]       = useState(pass?.comment || '');

  const [groups, setGroups]   = useState([]);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get('/api/skud/groups')
      .then((res) => setGroups(res.rows || []))
      .catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Конвертация Wiegand → Bolid по Enter. Bolid-код — ровно 16 символов;
  // короче — считаем Wiegand'ом и разворачиваем.
  const convertCode = () => {
    setCode((prev) => {
      const raw = prev.trim();
      if (raw.length === 0 || raw.length >= 16) return prev;
      const r = wiegandToBolid(raw);
      if (!r.success) {
        setError(r.error || 'Не удалось распознать код');
        return prev;
      }
      setError(null);
      return r.code;
    });
  };

  // Считыватель карт «печатает» как клавиатура и может начать ввод, когда
  // фокус не в поле кода (клик мимо, только что открытая модалка). Тогда
  // первый символ терялся, а остаток улетал в браузерный поиск/другие поля.
  // Пока открыта модалка СОЗДАНИЯ пропуска, перехватываем печатные символы
  // на window и направляем их в поле кода — скан попадает туда целиком,
  // где бы ни был фокус. Если фокус в другом поле ввода (даты, комментарий,
  // группа) — не мешаем, человек печатает осознанно.
  const codeInputRef = useRef(null);
  useEffect(() => {
    if (isEdit) return undefined;
    const onScanKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        convertCode();
        codeInputRef.current?.focus();
        return;
      }
      if (e.key.length !== 1) return; // служебные клавиши не трогаем
      e.preventDefault();
      setCode((prev) => (prev + e.key).toUpperCase());
      setError(null);
      codeInputRef.current?.focus();
    };
    window.addEventListener('keydown', onScanKey);
    return () => window.removeEventListener('keydown', onScanKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const submit = async (e) => {
    // Внешняя форма (например, StaffEditModal) — нашу submit'у не должна
    // ловить. preventDefault — для нашей собственной формы; stopPropagation —
    // на случай, если событие всплыло до родителя.
    e.preventDefault();
    e.stopPropagation();
    if (!isEdit && code.trim() === '') {
      setError('Введите код карты'); return;
    }
    if (accessLevelId === '' || accessLevelId === null) {
      setError('Выберите группу доступа — без неё карта не сможет открыть двери');
      return;
    }
    setSaving(true);
    setError(null);

    const body = {
      startDate: startDate || null,
      endDate:   endDate   || null,
      accessLevelId: accessLevelId === '' ? null : Number(accessLevelId),
      comment:   comment.trim() || null,
    };
    if (!isEdit) body.code = code.trim().toUpperCase();

    try {
      if (isEdit) {
        await api.put(`/api/skud/passes/${pass.skudId}`, body);
      } else {
        await api.post(`/api/skud/persons/${skudPersonId}/passes`, body);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  // Через portal, чтобы модалка не оказалась внутри другой формы
  // (например, StaffEditModal — там <form> на весь корпус, и наша submit-
  // кнопка могла бы случайно отправлять родителя). React-портал монтирует
  // нашу разметку прямо в document.body, выйдя из иерархии DOM родителя.
  return createPortal((
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--small" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{isEdit ? 'Пропуск СКУД' : 'Новый пропуск'}</h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Код карты *</label>
              <input
                ref={codeInputRef}
                className="sd-edit__input mono"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  // Enter «съедаем», чтобы случайно не отправить форму,
                  // и конвертируем Wiegand → Bolid (см. convertCode).
                  e.preventDefault();
                  convertCode();
                }}
                placeholder="приложите карту к считывателю или введите HEX и нажмите Enter"
                disabled={isEdit}
                autoFocus={!isEdit}
              />
              {isEdit ? (
                <div className="sd-edit__hint">
                  Код менять нельзя — это идентификатор карты в Orion. Если карту
                  потеряли — заведите новый пропуск, старый — пометьте на удаление.
                </div>
              ) : (
                <div className="sd-edit__hint">
                  Введите HEX-код с карты (Wiegand) и нажмите Enter — он
                  автоматически развернётся в формат Bolid (16 символов).
                  Если уже введены полные 16 символов Bolid-кода, Enter
                  ничего не сделает.
                </div>
              )}
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Действует с</label>
              <input className="sd-edit__input" type="date"
                     value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Действует по</label>
              <input className="sd-edit__input" type="date"
                     value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Группа доступа *</label>
              <select className="sd-edit__select" required
                      value={accessLevelId === null ? '' : String(accessLevelId)}
                      onChange={(e) => setAccess(e.target.value)}>
                <option value="">— выберите группу —</option>
                {groups.map((g) => (
                  <option key={g.skudId} value={g.skudId}>{g.name}</option>
                ))}
              </select>
              <div className="sd-edit__hint">
                Группа определяет, какие двери откроет карта — без неё пропуск
                не сможет открыть ничего. Подразделение тут ни при чём — оно
                про категорию человека (Сотрудники / Ученики / Родители) и
                автоматически берётся из карточки.
              </div>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Комментарий</label>
              <input className="sd-edit__input"
                     value={comment} onChange={(e) => setComment(e.target.value)}
                     placeholder="например, временная карта на месяц" />
            </div>
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (isEdit ? 'Сохранить' : 'Создать')}
            </button>
          </div>
        </form>
      </div>
    </div>
  ), document.body);
}

function toDateInput(v) {
  if (!v) return '';
  // 'YYYY-MM-DD HH:MM:SS' → 'YYYY-MM-DD'
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(v);
  return m ? m[1] : '';
}

function pad(n) { return String(n).padStart(2, '0'); }
function isoToday() {
  const d = new Date();
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}
function isoTodayPlusYears(n) {
  const d = new Date();
  d.setFullYear(d.getFullYear() + n);
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}
